import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TooltipService } from '@babybeet/angular-tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SettingsFacadeService } from '../../../services/facades/settings-facade.service';
import { ServerSentEventHandlerService } from '../../../services/mercure/server-sent-event-handler.service';
import { navListAnimation } from '../../../shared/animations';
import { HistoryNavigationComponent } from '../navbar/history/history-navigation.component';
import { SidebarButtonName } from './sidebar-button/sidebar-button.component';
import { SidebarButtonGroupComponent } from './sidebar-button-group/sidebar-button-group.component';
import { SidebarStatusIndicatorsComponent } from './sidebar-status-indicators/sidebar-status-indicators.component';
import { SidebarToggleButtonComponent } from './sidebar-toggle-button/sidebar-toggle-button.component';
import { SidebarUserProfileComponent } from './sidebar-user-profile/sidebar-user-profile.component';

export type SidebarButton = {
  name: SidebarButtonName;
  label: string;
  disabled: boolean;
  link?: string;
  svg?: string;
  icon_name?: string;
};

const slideInOut = trigger('slideInOut', [
  state('true', style({ width: '250px' })),
  state('false', style({ width: '64px' })),
  transition('false => true', animate('200ms ease-in')),
  transition('true => false', animate('200ms ease-out')),
]);

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    SidebarUserProfileComponent,
    SidebarButtonGroupComponent,
    SidebarToggleButtonComponent,
    SidebarStatusIndicatorsComponent,
    HistoryNavigationComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [slideInOut, navListAnimation],
})
export class SidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() clickSidebarButton = new EventEmitter<SidebarButton>();
  @Input() userData: { name: string; photoLink: string } = {
    name: 'Dr. Saki Lobinger',
    photoLink: '../assets/img/placeholder.jpg',
  };
  @Input() currentBreakpoint: string = '';

  // Define base buttons (always visible)
  private baseTopButtons: SidebarButton[] = [
    {
      name: 'start',
      label: 'COMPONENT.SIDEBAR.start',
      disabled: false,
      link: '/start',
      icon_name: 'home',
    },
    {
      name: 'organization',
      label: 'COMPONENT.SIDEBAR.organization',
      disabled: false,
      link: '/organization',
      icon_name: 'location_city',
    },
    {
      name: 'dashboard',
      label: 'COMPONENT.SIDEBAR.dashboard',
      disabled: false,
      link: '/dashboard',
      icon_name: 'dashboard',
    },
  ];

  private adminButton: SidebarButton = {
    name: 'admin',
    label: 'COMPONENT.SIDEBAR.admin',
    disabled: false,
    link: '/admin',
    icon_name: 'admin_panel_settings',
  };

  public get topButtons(): SidebarButton[] {
    if (this.isAdminMode) {
      return [...this.baseTopButtons, this.adminButton];
    }
    return this.baseTopButtons;
  }

  @Input() bottomButtons: SidebarButton[] = [
    {
      name: 'settings',
      label: 'COMPONENT.SIDEBAR.settings',
      disabled: false,
      link: '/settings',
      icon_name: 'settings',
    },
    {
      name: 'logout',
      label: 'COMPONENT.SIDEBAR.logout',
      disabled: false,
      icon_name: 'logout',
    },
  ];

  @Input() isExpanded: boolean = false;
  @Output() sidebarStateChanged = new EventEmitter<boolean>();
  @ViewChild('sidenav') sidenav!: MatSidenav;
  public locatorStatus: {
    remaining: number;
    processed: number;
    locatorLog: { locator: string; success: boolean }[];
  } = {
    remaining: 0,
    processed: 0,
    locatorLog: [],
  };
  public currentPath: string;
  private isTooltipOpen: boolean = false;
  private tooltipTimeout: number | undefined;
  private allSubs: Subscription[] = [];

  public isAdminMode: boolean = false;

  public constructor(
    private SSEHandler: ServerSentEventHandlerService,
    private readonly tooltipService: TooltipService,
    private location: Location,
    private settingsFacade: SettingsFacadeService,
    private router: Router,
  ) {
    this.currentPath = this.location.path();
  }

  public ngOnInit() {
    this.allSubs.push(
      this.SSEHandler.locatorStatus$.subscribe(status => {
        this.locatorStatus = status;
      }),
    );

    this.allSubs.push(
      this.settingsFacade.settings$.subscribe(settings => {
        this.isAdminMode = settings.adminMode || false;
      }),
    );

    this.allSubs.push(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          this.currentPath = event.url;
        }),
    );
  }

  public ngOnDestroy() {
    this.allSubs.forEach(s => s.unsubscribe());
  }

  public toggleTooltip(event: MouseEvent, templateRefContent: TemplateRef<any>) {
    if (this.isTooltipOpen) {
      this.onHideTooltip();
    } else {
      this.onShowTooltip(event, templateRefContent);
    }

    this.isTooltipOpen = !this.isTooltipOpen;
  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
    this.sidebarStateChanged.emit(this.isExpanded);
  }

  private onHideTooltip(): void {
    clearTimeout(this.tooltipTimeout);
    this.tooltipService.hide();
  }

  private onShowTooltip(event: MouseEvent, templateRefContent: TemplateRef<unknown>): void {
    const target = event.target as Element | null;
    if (target) {
      this.tooltipService.show(target, {
        content: templateRefContent,
        context: { $implicit: 'TemplateRef' },
        placement: 'horizontal',
        theme: 'light',
      });
    }
  }

  onClickOnItem(button: SidebarButton): void {
    this.clickSidebarButton.emit(button);

    if ((this.currentBreakpoint === 'xs' || this.currentBreakpoint === 'sm') && this.sidenav) {
      setTimeout(() => {
        if (this.sidenav && this.sidenav.opened) {
          this.sidenav.close();
        }
      }, 10);
    }
  }

  public closeSidebar(): void {
    if (this.isExpanded) {
      this.isExpanded = false;
      this.sidebarStateChanged.emit(false);
    }

    if (this.sidenav && this.sidenav.opened) {
      setTimeout(() => {
        this.sidenav.close();
      }, 0);
    }
  }

  ngAfterViewInit() {
    if (this.sidenav) {
      const sidenavAny = this.sidenav as any;
      if (Object.prototype.hasOwnProperty.call(sidenavAny, 'autoClose')) {
        sidenavAny.autoClose = false;
      }
      if (Object.prototype.hasOwnProperty.call(sidenavAny, 'closeOnNavigation')) {
        sidenavAny.closeOnNavigation = false;
      }
    }
  }
}
